import React from 'react';
import Icon from '../../../components/AppIcon';
import StatusIndicator from '../../../components/ui/StatusIndicator';
import ProfitPieChart from './ProfitPieChart';

const PRODUCTIVE_HOURS_LABELS = {
  daily: 'Productive Hours/Day',
  monthly: 'Productive Hours/Month',
  hourly: 'Productive Hours (Hourly)',
};

const CalculationResults = ({ calculations, currency, pricingBasis }) => {
  const currencySymbol = currency?.symbol || 'OMR';
  const productiveHoursLabel = PRODUCTIVE_HOURS_LABELS?.[pricingBasis] || 'Productive Hours/Month';

  const resultSections = [
    {
      title: 'Labor Costs',
      icon: 'Users',
      items: [
        { label: productiveHoursLabel, value: calculations?.productiveHoursPerMonth, format: 'hours' },
        { label: 'Cost per Productive Hour', value: calculations?.costPerProductiveHour, format: 'currency' },
        ...(calculations?.totalEngineerSpf > 0 ? [{ label: 'SPF (Omani Engineers)', value: calculations?.totalEngineerSpf, format: 'currency' }] : []),
        ...(calculations?.totalEngineerHealthInsurance > 0 ? [{ label: 'Health Insurance', value: calculations?.totalEngineerHealthInsurance, format: 'currency' }] : []),
        { label: 'Total Engineer Costs', value: calculations?.totalEngineerCosts, format: 'currency', highlight: true }
      ]
    },
    {
      title: 'Materials & Licenses',
      icon: 'Package',
      items: [
        { label: 'Materials Cost (with waste)', value: calculations?.totalMaterialsCost, format: 'currency' },
        { label: 'Licenses Cost', value: calculations?.totalLicensesCosts, format: 'currency' },
        { label: 'Combined M&L Cost', value: (calculations?.totalMaterialsCost || 0) + (calculations?.totalLicensesCosts || 0), format: 'currency', highlight: true }
      ]
    },
    ...(calculations?.totalOutsourceCosts > 0 ? [{
      title: 'Outsource Resources',
      icon: 'UserCheck',
      items: [
        { label: 'Total Outsource Cost', value: calculations?.totalOutsourceCosts, format: 'currency', highlight: true }
      ]
    }] : []),
    {
      title: 'Cost Breakdown',
      icon: 'Calculator',
      items: [
        { label: 'Direct Costs', value: calculations?.directCosts, format: 'currency' },
        { label: `Overhead (${calculations?.overheadPercentage}%)`, value: calculations?.overheadCosts, format: 'currency' },
        { label: `Contingency (${calculations?.contingencyPercentage}%)`, value: calculations?.contingencyCosts, format: 'currency' },
        { label: 'Total Project Cost', value: calculations?.totalCost, format: 'currency', highlight: true }
      ]
    },
    {
      title: 'Pricing & Profit',
      icon: 'DollarSign',
      items: [
        { label: `Sell Price (${calculations?.marginPercentage}% margin)`, value: calculations?.sellPrice, format: 'currency', highlight: true },
        { label: 'Gross Profit', value: calculations?.grossProfit, format: 'currency' },
        { label: 'Profit Margin', value: calculations?.profitMarginPercentage, format: 'percentage' }
      ]
    }
  ];

  const formatValue = (value, format) => {
    if (format === 'currency') {
      return `OMR ${(value || 0)?.toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}`;
    }
    if (format === 'percentage') {
      return `${value?.toFixed(2)}%`;
    }
    if (format === 'hours') {
      return `${value?.toFixed(0)} hrs`;
    }
    return value?.toLocaleString('en-US');
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-foreground">Calculation Results</h3>
        <StatusIndicator status="completed" label="Calculated" size="sm" />
      </div>
      {resultSections?.map((section, idx) => (
        <div key={idx} className="bg-card border border-border rounded-lg p-4 md:p-6">
          <div className="flex items-center gap-2 mb-4">
            <Icon name={section?.icon} size={20} color="var(--color-primary)" />
            <h4 className="text-sm font-semibold text-foreground">{section?.title}</h4>
          </div>
          <div className="space-y-3">
            {section?.items?.map((item, itemIdx) => (
              <div
                key={itemIdx}
                className={`flex items-start justify-between gap-2 py-2 ${
                  item?.highlight ? 'border-t-2 border-primary/20 pt-3' : ''
                }`}
              >
                <span className={`text-sm flex-1 min-w-0 ${item?.highlight ? 'font-semibold text-foreground' : 'text-muted-foreground'}`}>
                  {item?.label}
                </span>
                <span className={`text-sm flex-shrink-0 text-right ${item?.highlight ? 'font-bold text-primary text-base' : 'font-medium text-foreground'}`}>
                  {formatValue(item?.value, item?.format)}
                </span>
              </div>
            ))}
          </div>
        </div>
      ))}
      <ProfitPieChart calculations={calculations} />
      <div className="bg-success/10 border border-success/20 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Icon name="CheckCircle2" size={20} color="var(--color-success)" />
          <div className="flex-1">
            <div className="text-sm font-semibold text-success mb-1">Calculation Complete</div>
            <div className="text-xs text-muted-foreground">
              All costs have been calculated using Excel-matching logic with delivery mode factors, waste percentages, and commercial configurations applied.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CalculationResults;